import React from "react";

import { Button } from "../../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import PublicFormFieldsEditor from "./PublicFormFieldsEditor";

export default function PublicFormEditor({
  form,
  onChange,
  onSave,
  saving,
  baseUrl,
  canUseEmailTracking,
  canUseMarketingConsent,
  onCopy,
}) {
  if (!form) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Kein Formular ausgewählt</CardTitle>
          <CardDescription>
            Wählen Sie links ein Formular aus oder legen Sie ein neues Formular an.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const standaloneUrl = `${baseUrl}/demo/${form.form_key}`;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Basis</CardTitle>
          <CardDescription>Interne Bezeichnung, Art und Aktivierungsstatus des Formulars.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                data-testid="public-form-editor-name"
                value={form.name || ""}
                onChange={(event) => onChange({ ...form, name: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Formularart</Label>
              <select
                data-testid="public-form-editor-kind"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.form_kind || "general_contact"}
                onChange={(event) => onChange({ ...form, form_kind: event.target.value })}
              >
                <option value="general_contact">Allgemeiner Kontakt</option>
                <option value="demo_request">Demo-Anfrage</option>
                <option value="strategy_call">Strategiegespräch</option>
                <option value="whitepaper_download">Whitepaper-Download</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.is_active)}
              onChange={(event) => onChange({ ...form, is_active: event.target.checked })}
            />
            Formular aktiv
          </label>

          <div className="rounded-xl border border-border/70 p-3 text-sm text-muted-foreground">
            Aktivierung ist nur mit gültiger Datenschutzerklärung-URL und gültiger Impressum-URL möglich.
          </div>

          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
            <Input readOnly value={standaloneUrl} />
            <Button type="button" variant="outline" onClick={() => onCopy(standaloneUrl, "Standalone-Link")}>
              Link kopieren
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Texte</CardTitle>
          <CardDescription>Bearbeiten Sie Headline, Intro, CTA und Danke-Texte des Formulars.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Headline</Label>
            <Input
              data-testid="public-form-editor-headline"
              value={form.headline || ""}
              onChange={(event) => onChange({ ...form, headline: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Subline</Label>
            <Input
              data-testid="public-form-editor-subline"
              value={form.subline || ""}
              onChange={(event) => onChange({ ...form, subline: event.target.value })}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Intro</Label>
            <Textarea value={form.intro_text || ""} onChange={(event) => onChange({ ...form, intro_text: event.target.value })} rows={3} />
          </div>
          <div className="space-y-2">
            <Label>Button-Text</Label>
            <Input value={form.button_text || ""} onChange={(event) => onChange({ ...form, button_text: event.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Danke-Titel</Label>
            <Input
              value={form.thank_you_title || ""}
              onChange={(event) => onChange({ ...form, thank_you_title: event.target.value })}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Danke-Text</Label>
            <Textarea
              value={form.thank_you_text || ""}
              onChange={(event) => onChange({ ...form, thank_you_text: event.target.value })}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Felder</CardTitle>
          <CardDescription>Bestehende Standardfelder können sichtbar, erforderlich und umsortiert werden.</CardDescription>
        </CardHeader>
        <CardContent>
          <PublicFormFieldsEditor
            value={form.field_config || []}
            onChange={(fieldConfig) => onChange({ ...form, field_config: fieldConfig })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Datenschutz & Einwilligungen</CardTitle>
          <CardDescription>
            Bearbeiten Sie Privacy-Texte, Legal-URLs und optionalen Consent-Text.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Privacy Checkbox Text</Label>
            <Textarea
              value={form.privacy_checkbox_text || ""}
              onChange={(event) => onChange({ ...form, privacy_checkbox_text: event.target.value })}
              rows={2}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Privacy URL</Label>
              <Input
                value={form.privacy_policy_url || ""}
                onChange={(event) => onChange({ ...form, privacy_policy_url: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Impressum URL</Label>
              <Input
                value={form.imprint_url || ""}
                onChange={(event) => onChange({ ...form, imprint_url: event.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Legal Notice</Label>
            <Textarea
              value={form.legal_notice_text || ""}
              onChange={(event) => onChange({ ...form, legal_notice_text: event.target.value })}
              rows={2}
            />
          </div>
          <div className="rounded-xl border border-border/70 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">Marketing Consent</div>
                {!canUseMarketingConsent ? (
                  <div className="text-xs text-muted-foreground">
                    Marketing-Consent ist im aktuellen Tarif deaktiviert und wird nicht wirksam ausgespielt.
                  </div>
                ) : null}
              </div>
              <input
                type="checkbox"
                data-testid="public-form-marketing-consent-enabled-switch"
                checked={Boolean(form.marketing_consent_enabled)}
                disabled={!canUseMarketingConsent}
                onChange={(event) => onChange({ ...form, marketing_consent_enabled: event.target.checked })}
              />
            </div>
            <Textarea
              className="mt-3"
              data-testid="public-form-marketing-consent-text"
              disabled={!canUseMarketingConsent}
              value={form.marketing_consent_text || ""}
              onChange={(event) => onChange({ ...form, marketing_consent_text: event.target.value })}
              rows={2}
            />
          </div>
          <div className="rounded-xl border border-border/70 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">E-Mail-Tracking Consent</div>
                {!canUseEmailTracking ? (
                  <div className="text-xs text-muted-foreground">
                    E-Mail-Tracking ist im aktuellen Tarif deaktiviert und wird nicht wirksam ausgespielt.
                  </div>
                ) : null}
              </div>
              <input
                type="checkbox"
                data-testid="public-form-email-tracking-enabled-switch"
                checked={Boolean(form.email_tracking_consent_enabled)}
                disabled={!canUseEmailTracking}
                onChange={(event) => onChange({ ...form, email_tracking_consent_enabled: event.target.checked })}
              />
            </div>
            <Textarea
              className="mt-3"
              data-testid="public-form-email-tracking-text"
              disabled={!canUseEmailTracking}
              value={form.email_tracking_consent_text || ""}
              onChange={(event) => onChange({ ...form, email_tracking_consent_text: event.target.value })}
              rows={2}
            />
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={onSave}
              disabled={saving}
              data-testid="public-form-editor-save-button"
            >
              {saving ? "Speichern ..." : "Formular speichern"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
